import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/lib/i18n";
import LetterWizard from "./LetterWizard";
import LetterEditor from "./LetterEditor";
import { Plus, Trash2, Eye, Clock, BookMarked, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { softDeleteWithUndo } from "@/lib/softDelete";
import { MatchScoreBadge } from "./MatchScoreBadge";
import { useConfirm } from "@/components/ConfirmDialog";

const RETENTION_OPTIONS = [
  [30, "ltRetention30"], [90, "ltRetention90"], [180, "ltRetention180"],
  [365, "ltRetention365"], [0, "ltRetentionForever"],
];

export default function LettersPanel({ onRegistered, onOpenJob, onNavigateToCvs }) {
  const confirm = useConfirm();
  const { t, lang } = useT();
  const { user, setUser } = useAuth();
  const [letters, setLetters] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editorId, setEditorId] = useState(null);
  const [editorAutoImprove, setEditorAutoImprove] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/letters");
      setLetters(data);
    } catch {
      toast.error(t("ltLoadFailed"));
      setLetters([]);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!(await confirm(t("ltConfirmDelete")))) return;
    const removedIndex = (letters || []).findIndex((l) => l.id === id);
    const removedItem = (letters || [])[removedIndex];
    if (!removedItem) return;
    softDeleteWithUndo({
      kind: "letter",
      id,
      apiDelete: () => api.delete(`/letters/${id}`),
      optimisticHide: () => setLetters((prev) => (prev || []).filter((l) => l.id !== id)),
      restore: () => setLetters((prev) => {
        const list = prev || [];
        if (list.some((l) => l.id === id)) return list;
        const next = [...list];
        next.splice(Math.min(removedIndex, next.length), 0, removedItem);
        return next;
      }),
      labels: {
        success: t("ltDeleted"),
        undone: t("rbUndone"),
        undo: t("undo"),
        error: t("dashDeleteFailed"),
      },
    });
  };

  const setRetention = async (days) => {
    try {
      const { data } = await api.patch("/auth/preferences", { letter_retention_days: days });
      setUser?.(data);
      toast.success(t("ltRetentionSaved"));
      load();
    } catch {
      toast.error(t("ltSaveFailed"));
    }
  };

  return (
    <div data-testid="letters-panel">
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <p className="font-serif text-2xl italic text-neutral-800">{t("ltSubtitle")}</p>
        <div className="flex items-end gap-4 flex-wrap">
          <div>
            <label className="ed-label flex items-center gap-1.5">
              <Clock size={11} strokeWidth={1.5} /> {t("ltRetention")}
            </label>
            <select
              data-testid="letters-retention"
              className="ed-input"
              value={user?.letter_retention_days ?? 90}
              onChange={(e) => setRetention(parseInt(e.target.value, 10))}
              title={t("ltRetentionHint")}
            >
              {RETENTION_OPTIONS.map(([days, key]) => (
                <option key={days} value={days}>{t(key)}</option>
              ))}
            </select>
          </div>
          <button data-testid="letters-new" className="ed-btn" onClick={() => setWizardOpen(true)}>
            <Plus size={14} strokeWidth={1.5} /> {t("ltNew")}
          </button>
        </div>
      </div>

      {letters === null ? (
        <div className="ed-card p-10 text-center"><p className="ed-eyebrow">{t("loading")}</p></div>
      ) : letters.length === 0 ? (
        <div className="ed-card p-14 text-center" data-testid="letters-empty">
          <p className="text-sm text-neutral-500 italic font-serif">{t("ltEmpty")}</p>
        </div>
      ) : (
        <div className="ed-card overflow-x-auto">
          <table className="ed-table">
            <thead>
              <tr>
                <th>{t("dashColPosition")}</th>
                <th>{t("dashColCompany")}</th>
                <th>{t("ltBasedOn")}</th>
                <th>{t("ltMatchScore")}</th>
                <th>{t("dashLabelType")}</th>
                <th>{t("ltCreatedAt")}</th>
                <th className="text-right">{t("dashColActions")}</th>
              </tr>
            </thead>
            <tbody>
              {letters.map((l, i) => (
                <tr key={l.id} data-testid={`letter-row-${i}`} className="cursor-pointer" onClick={() => setEditorId(l.id)}>
                  <td className="font-serif text-lg leading-tight">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span>{l.job_title}</span>
                      {l.job_id && onOpenJob && (
                        <button
                          data-testid={`letter-in-register-${i}`}
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onOpenJob(l.job_id); }}
                          title={t("ltInRegisterHint")}
                          className="font-mono text-[0.55rem] uppercase tracking-[0.15em] border border-emerald-700 bg-emerald-50 text-emerald-800 hover:bg-emerald-700 hover:text-white transition-colors px-2 py-0.5 inline-flex items-center gap-1"
                        >
                          <BookMarked size={9} strokeWidth={1.75} /> {t("ltInRegisterBadge")}
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="text-sm text-neutral-700">{l.company}</td>
                  <td className="text-sm text-neutral-700 italic">{l.resume_name}</td>
                  <td data-testid={`letter-match-${i}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <MatchScoreBadge score={l.match_score} />
                      {l.match_score !== null && l.match_score !== undefined && l.match_score < 90 && (
                        <button
                          data-testid={`letter-improve-cta-${i}`}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            // Optimistic: hide the pulse immediately + persist server-side.
                            setLetters((prev) => (prev || []).map((row) => row.id === l.id
                              ? { ...row, match_wizard_opened_at: row.match_wizard_opened_at || new Date().toISOString() }
                              : row));
                            api.post(`/letters/${l.id}/mark-wizard-opened`).catch(() => {});
                            setEditorAutoImprove(true);
                            setEditorId(l.id);
                          }}
                          title={t("ltImproveCta")}
                          className={
                            (!l.match_wizard_opened_at && !(l.match_history && l.match_history.length > 0)
                              ? "letter-improve-pulse border-amber-500 bg-amber-100 text-amber-900 hover:bg-amber-500 hover:text-white "
                              : "border-neutral-900 bg-white hover:bg-neutral-900 hover:text-white ") +
                            "font-mono text-[0.55rem] uppercase tracking-[0.15em] border transition-colors px-2 py-0.5 inline-flex items-center gap-1"
                          }
                        >
                          <Sparkles size={9} strokeWidth={1.75} /> {t("ltImproveBadge")}
                        </button>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className="font-mono text-[0.65rem] uppercase tracking-[0.15em] border border-neutral-900 px-2 py-0.5">
                      {l.lang}
                    </span>
                  </td>
                  <td className="text-sm font-mono text-neutral-700">
                    {new Date(l.created_at).toLocaleDateString(lang === "en" ? "en-GB" : "nl-NL")}
                  </td>
                  <td className="text-right">
                    <div className="flex justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                      <button
                        data-testid={`letter-open-${i}`}
                        onClick={() => setEditorId(l.id)}
                        className="p-2 border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors"
                        title={t("ltOpen")}
                      >
                        <Eye size={14} strokeWidth={1.5} />
                      </button>
                      <button
                        data-testid={`letter-delete-${i}`}
                        onClick={() => remove(l.id)}
                        className="p-2 border border-neutral-300 hover:border-rose-700 hover:bg-rose-700 hover:text-white transition-colors"
                        title={t("delete")}
                      >
                        <Trash2 size={14} strokeWidth={1.5} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {wizardOpen && (
        <LetterWizard
          prefillJob={null}
          onClose={() => setWizardOpen(false)}
          onDone={(letter) => { setWizardOpen(false); load(); setEditorId(letter.id); }}
        />
      )}
      {editorId && (
        <LetterEditor
          letterId={editorId}
          autoOpenImprove={editorAutoImprove}
          onClose={(didChange) => {
            setEditorId(null);
            setEditorAutoImprove(false);
            // Only reload when the editor actually mutated the letter — avoids
            // the jarring "list re-renders on every close" that users reported.
            if (didChange) load();
          }}
          onRegistered={onRegistered}
          onNavigateToCvs={onNavigateToCvs}
        />
      )}
    </div>
  );
}
